import type { AdminMeeting, Moderator } from '@/entities/admin';
import { adminApi } from '@/features/admin';
import { cn } from '@/shared/lib';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Skeleton } from '@/shared/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertCircle,
  Archive,
  Calendar,
  CheckCircle,
  Clock,
  MapPin,
  Trash2,
  UserCheck,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

type Status = 'pending' | 'assigned' | 'archived';

const statusTabs: { value: Status; label: string; icon: typeof Clock; empty: string }[] = [
  { value: 'pending', label: 'En attente', icon: Clock, empty: 'Aucun RDV en attente' },
  { value: 'assigned', label: 'Assignés', icon: UserCheck, empty: 'Aucun RDV assigné' },
  { value: 'archived', label: 'Archivés', icon: Archive, empty: 'Aucun RDV archivé' },
];

type Confirm = { kind: 'archive' | 'delete'; meeting: AdminMeeting } | null;

function person(meeting: AdminMeeting, side: 'A' | 'B') {
  const nodeId = side === 'A' ? meeting.nodeIdA : meeting.nodeIdB;
  const username = side === 'A' ? meeting.usernameA : meeting.usernameB;
  const first = side === 'A' ? meeting.firstnameA : meeting.firstnameB;
  const last = side === 'A' ? meeting.lastnameA : meeting.lastnameB;
  const city = side === 'A' ? meeting.zipA : meeting.zipB;
  return { nodeId, username, fullName: [first, last].filter(Boolean).join(' '), city };
}

function Person({ meeting, side }: { meeting: AdminMeeting; side: 'A' | 'B' }) {
  const p = person(meeting, side);
  return (
    <div className="min-w-0">
      <Link to={`/admin/users/${p.nodeId}`} className="font-medium hover:underline">
        {p.username}
      </Link>
      {p.fullName && <div className="truncate text-xs text-muted-foreground">{p.fullName}</div>}
      {p.city && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3 shrink-0" />
          {p.city}
        </div>
      )}
    </div>
  );
}

function when(date?: string) {
  return date ? formatDistanceToNow(new Date(date), { addSuffix: true, locale: fr }) : '-';
}

export default function AdminMeetingsPage() {
  const [status, setStatus] = useState<Status>('pending');
  const [confirm, setConfirm] = useState<Confirm>(null);
  const queryClient = useQueryClient();

  const meetingsQuery = useQuery({
    queryKey: ['admin-meetings', status],
    queryFn: () => adminApi.getMeetings(status),
  });
  const { data: moderators = [] } = useQuery({
    queryKey: ['admin-moderators'],
    queryFn: adminApi.getModerators,
  });
  const meetings = meetingsQuery.data ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['admin-meetings'] });
  const onError = () => toast.error("L'action a échoué");

  const assignMutation = useMutation({
    mutationFn: ({ interactionId, moderatorId }: { interactionId: number; moderatorId: number }) =>
      adminApi.assignModerator(interactionId, moderatorId),
    onSuccess: () => {
      toast.success('Modérateur assigné');
      refresh();
    },
    onError,
  });
  const validateMutation = useMutation({
    mutationFn: (interactionId: number) => adminApi.validateMeeting(interactionId),
    onSuccess: () => {
      toast.success('RDV validé');
      refresh();
    },
    onError,
  });
  const archiveMutation = useMutation({
    mutationFn: (interactionId: number) => adminApi.archiveMeeting(interactionId),
    onSuccess: () => {
      toast.success('RDV archivé');
      refresh();
    },
    onError,
  });
  const deleteMutation = useMutation({
    mutationFn: (interactionId: number) => adminApi.deleteMeeting(interactionId),
    onSuccess: () => {
      toast.success('RDV supprimé');
      refresh();
    },
    onError,
  });

  const busy =
    assignMutation.isPending ||
    validateMutation.isPending ||
    archiveMutation.isPending ||
    deleteMutation.isPending;

  const ModeratorCell = ({ meeting }: { meeting: AdminMeeting }) =>
    status === 'archived' ? (
      <Badge variant="outline">
        {moderators.find((m: Moderator) => m.id === meeting.moderatorId)?.username ??
          (meeting.moderatorId ? `#${meeting.moderatorId}` : 'Non assigné')}
      </Badge>
    ) : (
      <Select
        value={meeting.moderatorId ? String(meeting.moderatorId) : undefined}
        onValueChange={(v) =>
          assignMutation.mutate({ interactionId: meeting.interactionId, moderatorId: Number(v) })
        }
        disabled={busy || moderators.length === 0}
      >
        <SelectTrigger className="w-full min-w-40" aria-label="Assigner un modérateur">
          <SelectValue placeholder={moderators.length ? 'Non assigné' : 'Aucun modérateur'} />
        </SelectTrigger>
        <SelectContent>
          {moderators.map((mod) => (
            <SelectItem key={mod.id} value={String(mod.id)}>
              {mod.username}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );

  const Actions = ({ meeting }: { meeting: AdminMeeting }) => (
    <div className="flex flex-wrap gap-1">
      {status !== 'archived' && !meeting.isValidated && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => validateMutation.mutate(meeting.interactionId)}
          disabled={busy}
        >
          <CheckCircle className="mr-1 h-4 w-4 text-success" />
          Valider
        </Button>
      )}
      {status !== 'archived' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => setConfirm({ kind: 'archive', meeting })}
          disabled={busy}
        >
          <Archive className="mr-1 h-4 w-4" />
          Archiver
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        aria-label="Supprimer"
        onClick={() => setConfirm({ kind: 'delete', meeting })}
        disabled={busy}
      >
        <Trash2 className="h-4 w-4 text-destructive" />
      </Button>
    </div>
  );

  const StatusBadge = ({ meeting }: { meeting: AdminMeeting }) =>
    meeting.isValidated ? (
      <Badge className="bg-success text-white">Validé</Badge>
    ) : (
      <Badge variant="outline">À valider</Badge>
    );

  const current = statusTabs.find((t) => t.value === status) ?? statusTabs[0];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold sm:text-3xl">Rendez-vous</h1>
        <Badge variant="outline">
          <Calendar className="mr-1 h-3 w-3" />
          {meetings.length} RDV
        </Badge>
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2" role="tablist">
        {statusTabs.map((tab) => (
          <Button
            key={tab.value}
            role="tab"
            aria-selected={status === tab.value}
            variant={status === tab.value ? 'default' : 'outline'}
            onClick={() => setStatus(tab.value)}
          >
            <tab.icon className="mr-2 h-4 w-4" />
            {tab.label}
          </Button>
        ))}
      </div>

      {meetingsQuery.isError ? (
        <Card>
          <CardContent className="flex items-center gap-3 text-destructive">
            <AlertCircle className="h-5 w-5" />
            Impossible de charger les rendez-vous.
            <Button variant="outline" size="sm" onClick={() => meetingsQuery.refetch()}>
              Réessayer
            </Button>
          </CardContent>
        </Card>
      ) : meetingsQuery.isLoading ? (
        <Card>
          <CardContent className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </CardContent>
        </Card>
      ) : meetings.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            <Calendar className="mx-auto mb-4 h-12 w-12 opacity-50" />
            {current.empty}
          </CardContent>
        </Card>
      ) : (
        <div className={cn('transition-opacity', meetingsQuery.isFetching && 'opacity-60')}>
          {/* Desktop */}
          <Card className="hidden overflow-hidden lg:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Membres</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Modérateur</TableHead>
                  <TableHead>Commentaire</TableHead>
                  <TableHead>Mis à jour</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {meetings.map((meeting) => (
                  <TableRow key={meeting.interactionId} data-testid="admin-meeting-row">
                    <TableCell>
                      <div className="flex items-start gap-4">
                        <Person meeting={meeting} side="A" />
                        <span className="pt-0.5 text-muted-foreground">↔</span>
                        <Person meeting={meeting} side="B" />
                      </div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        #{meeting.interactionId}
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge meeting={meeting} />
                    </TableCell>
                    <TableCell className="w-48">
                      <ModeratorCell meeting={meeting} />
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm">
                      {meeting.comment || '-'}
                    </TableCell>
                    <TableCell className="text-sm whitespace-nowrap">
                      {when(meeting.updatedAt)}
                    </TableCell>
                    <TableCell>
                      <Actions meeting={meeting} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile / tablet */}
          <div className="space-y-3 lg:hidden">
            {meetings.map((meeting) => (
              <Card key={meeting.interactionId} data-testid="admin-meeting-card">
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-muted-foreground">
                      #{meeting.interactionId} · {when(meeting.updatedAt)}
                    </span>
                    <StatusBadge meeting={meeting} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Person meeting={meeting} side="A" />
                    <Person meeting={meeting} side="B" />
                  </div>
                  {meeting.comment && <p className="text-sm">{meeting.comment}</p>}
                  <ModeratorCell meeting={meeting} />
                  <Actions meeting={meeting} />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === 'delete'
                ? 'Supprimer ce rendez-vous ?'
                : 'Archiver ce rendez-vous ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm &&
                `${confirm.meeting.usernameA} ↔ ${confirm.meeting.usernameB}. ${
                  confirm.kind === 'delete'
                    ? "La mise en relation est définitivement supprimée, c'est irréversible."
                    : 'Le rendez-vous passe dans les archives.'
                }`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className={
                confirm?.kind === 'delete' ? 'bg-destructive hover:bg-destructive/90' : undefined
              }
              onClick={() => {
                if (!confirm) return;
                if (confirm.kind === 'delete') deleteMutation.mutate(confirm.meeting.interactionId);
                else archiveMutation.mutate(confirm.meeting.interactionId);
                setConfirm(null);
              }}
            >
              {confirm?.kind === 'delete' ? 'Supprimer' : 'Archiver'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
